<template>
  <div id="app-container">
    <AppTopBar
      :username="userInfo.username || 'admin'"
      :show-admin="userInfo.rol === 'admin' || userInfo.rol === 'superadmin'"
      :show-logout="true"
      :show-vista-toggle="true"
      :vista-actual="vistaActual"
      :conteo-marketplace="conteoVistas.marketplace"
      :conteo-web="conteoVistas.web"
      @logout="logout"
      @change-vista="cambiarVista"
    />

    <div class="view-content">
      <div class="view-header">
        <span class="view-kicker">HEATMAPS</span>
        <h1 class="view-title">Productos & Heatmaps</h1>
        <span class="view-meta">Explorar productos, comparar precios y armar mapas de calor</span>
      </div>

      <div class="exploration-panel">
        <!-- Sub-tabs -->
        <div class="main-tabs-bar">
          <button
            @click="mainTab = 'productos'"
            class="main-tab"
            :class="{ active: mainTab === 'productos' }"
          >
            PRODUCTOS
          </button>
          <button
            @click="mainTab = 'heatmap'"
            class="main-tab"
            :class="{ active: mainTab === 'heatmap' }"
          >
            HEATMAP EN VIVO
            <span v-if="heatmapSelection.length > 0" class="tab-badge">{{ heatmapSelection.length }}</span>
          </button>
          <button
            @click="mainTab = 'guardados'"
            class="main-tab"
            :class="{ active: mainTab === 'guardados' }"
          >
            GUARDADOS
          </button>
        </div>

        <!-- TAB 1: PRODUCTOS -->
        <div v-show="mainTab === 'productos'" class="dashboard-layout">
          <div class="product-list-panel">
            <h2>Productos</h2>
            <div class="filter-controls">
              <button @click="filterStatus = 'Todos'" :class="{ active: filterStatus === 'Todos' }">
                Todos <span class="filter-count">{{ productos.length }}</span>
              </button>
              <button @click="filterStatus = 'Competitivo'" :class="{ active: filterStatus === 'Competitivo' }">
                Competitivos <span class="filter-count">{{ totalCompetitivos }}</span>
              </button>
              <button @click="filterStatus = 'Exclusivo'" :class="{ active: filterStatus === 'Exclusivo' }">
                Exclusivos <span class="filter-count">{{ totalExclusivos }}</span>
              </button>
            </div>
            <input type="text" v-model="searchTerm" placeholder="Buscar producto..." class="search-bar">
            <ul class="product-list">
              <li v-for="product in filteredProducts" :key="product.codigo" @click="selectProduct(product)" :class="{ selected: product.codigo === selectedProduct?.codigo }">
                <span v-if="activeHeatmapCodes.has(String(product.codigo))" class="active-hm-dot" title="En heatmap activo"></span>
                <span class="product-code">[{{ product.codigo }}]</span>
                {{ product.descripcion }}
              </li>
            </ul>
          </div>

          <div class="product-detail-panel">
            <div class="tab-content">
              <div v-if="!selectedProduct" class="placeholder">
                Seleccioná un producto de la lista para ver su detalle.
              </div>
              <ComparadorProducto
                v-else
                :key="selectedProduct.codigo + '-' + vistaActual"
                :codigo="String(selectedProduct.codigo)"
                :initial-vista="vistaActual"
                :hide-search="true"
              />
            </div>
          </div>
        </div>

        <!-- TAB 2: HEATMAP EN VIVO -->
        <div v-show="mainTab === 'heatmap'" class="dashboard-layout">
          <div class="product-list-panel">
            <h2>Seleccionar Productos</h2>
            <div class="filter-controls">
              <button @click="filterStatus = 'Todos'" :class="{ active: filterStatus === 'Todos' }">
                Todos <span class="filter-count">{{ productos.length }}</span>
              </button>
              <button @click="filterStatus = 'Competitivo'" :class="{ active: filterStatus === 'Competitivo' }">
                Competitivos <span class="filter-count">{{ totalCompetitivos }}</span>
              </button>
              <button @click="filterStatus = 'Exclusivo'" :class="{ active: filterStatus === 'Exclusivo' }">
                Exclusivos <span class="filter-count">{{ totalExclusivos }}</span>
              </button>
            </div>
            <input type="text" v-model="searchTerm" placeholder="Buscar producto..." class="search-bar">
            <ul class="product-list">
              <li v-for="product in filteredProducts" :key="product.codigo">
                <input type="checkbox" :value="product.codigo" v-model="heatmapSelection" class="canasta-checkbox">
                <span v-if="activeHeatmapCodes.has(String(product.codigo))" class="active-hm-dot"></span>
                <span class="product-code">[{{ product.codigo }}]</span>
                {{ product.descripcion }}
              </li>
            </ul>
          </div>

          <div class="product-detail-panel">
            <div class="tab-content">
              <div v-if="heatmapSelection.length > 0" class="heatmap-constructor">
                <h3>Constructor de Heatmap ({{ heatmapSelection.length }} productos)</h3>
                <div class="controls">
                  <button @click="generarHeatmap" class="generate-btn"><PhChartBar :size="12" weight="bold" /> Generar</button>
                  <button @click="saveCurrentHeatmap" class="save-btn"><PhFloppyDisk :size="12" weight="bold" /> Guardar vista</button>
                  <button @click="clearHeatmapSelection" class="clear-btn">Limpiar</button>
                </div>
              </div>
              <div v-else class="empty-heatmap">
                <p>No hay productos seleccionados.</p>
                <p class="hint">Tildá productos a la izquierda para armar tu heatmap.</p>
              </div>

              <div v-if="heatmapSelection.length > 0" class="heatmap-preview">
                <div class="preview-section">
                  <div class="preview-title">
                    Productos seleccionados
                    <span class="count-chip">{{ heatmapSelection.length }}</span>
                  </div>
                  <ul class="selected-products">
                    <li v-for="p in selectedProductsDetail" :key="p.codigo">
                      <span class="prod-code">[{{ p.codigo }}]</span>
                      <span class="prod-desc">{{ p.descripcion }}</span>
                      <button class="remove-btn" @click="quitarDeSeleccion(p.codigo)" title="Quitar"><PhX :size="11" weight="bold" /></button>
                    </li>
                  </ul>
                </div>
              </div>

              <HeatmapPrecios v-if="heatmapData.length > 0" :heatmap-data="heatmapData" />
            </div>
          </div>
        </div>

        <!-- TAB 3: GUARDADOS -->
        <div v-show="mainTab === 'guardados'" class="guardados-tab">
          <SavedHeatmapsPanel ref="savedHeatmapsRef" @edit-heatmap="handleEditHeatmapAndSwitch" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

import AppTopBar from '../components/AppTopBar.vue';
import ComparadorProducto from '../components/ComparadorProducto.vue';
import HeatmapPrecios from '../components/HeatmapPrecios.vue';
import SavedHeatmapsPanel from '../components/SavedHeatmapsPanel.vue';
import { PhChartBar, PhFloppyDisk, PhX } from '@phosphor-icons/vue';

const router = useRouter();
const API_BASE_URL = 'https://dash-visaovip.kompara.tech/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('token_precios');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const userInfo = ref({ username: '', rol: '' });
const vistaActual = ref(localStorage.getItem('vista_actual') || 'web');
const conteoVistas = ref({ web: 0, marketplace: 0 });

const mainTab = ref('productos');
const productos = ref([]);
const filterStatus = ref('Todos');
const searchTerm = ref('');
const selectedProduct = ref(null);

const heatmapSelection = ref([]);
const heatmapData = ref([]);
const activeHeatmapCodes = ref(new Set());

const savedHeatmapsRef = ref(null);

const totalCompetitivos = computed(() =>
  productos.value.filter(p => p.status === 'Competitivo').length
);
const totalExclusivos = computed(() =>
  productos.value.filter(p => p.status === 'Exclusivo').length
);

const filteredProducts = computed(() => {
  let arr = productos.value;
  if (filterStatus.value !== 'Todos') {
    arr = arr.filter(p => p.status === filterStatus.value);
  }
  if (searchTerm.value) {
    const t = searchTerm.value.toLowerCase();
    arr = arr.filter(p =>
      String(p.codigo).toLowerCase().includes(t) ||
      String(p.descripcion).toLowerCase().includes(t)
    );
  }
  return arr.slice(0, 200);
});

const selectedProductsDetail = computed(() =>
  productos.value.filter(p => heatmapSelection.value.includes(p.codigo))
);

const cambiarVista = (v) => {
  vistaActual.value = v;
  localStorage.setItem('vista_actual', v);
  loadProductos();
};

const logout = () => {
  localStorage.removeItem('token_precios');
  router.push('/');
  window.location.reload();
};

const selectProduct = (p) => { selectedProduct.value = p; };

const clearHeatmapSelection = () => {
  heatmapSelection.value = [];
  heatmapData.value = [];
};
const quitarDeSeleccion = (codigo) => {
  heatmapSelection.value = heatmapSelection.value.filter(c => c !== codigo);
};

const loadProductos = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/productos`, {
      ...getAuthHeaders(), params: { vista: vistaActual.value }
    });
    productos.value = res.data;
  } catch (e) { console.error(e); }
};

const generarHeatmap = async () => {
  if (heatmapSelection.value.length === 0) return;
  try {
    const url = `${API_BASE_URL}/heatmap/dinamico?vista=${vistaActual.value}`;
    const res = await axios.post(url, {
      codigos: heatmapSelection.value
    }, getAuthHeaders());
    heatmapData.value = res.data.productos;
  } catch (e) { console.error(e); alert('Error generando heatmap'); }
};

const saveCurrentHeatmap = async () => {
  const name = prompt('Nombre para esta vista:');
  if (!name) return;
  try {
    await axios.post(`${API_BASE_URL}/saved-heatmaps/`, {
      name,
      product_codes: heatmapSelection.value,
      vista: vistaActual.value
    }, getAuthHeaders());
    alert('Vista guardada');
    if (savedHeatmapsRef.value) await savedHeatmapsRef.value.fetchSavedHeatmaps();
  } catch (e) { alert('Error guardando vista'); }
};

const handleEditHeatmapAndSwitch = async (hm) => {
  mainTab.value = 'heatmap';
  try {
    const res = await axios.get(`${API_BASE_URL}/saved-heatmaps/${hm.id}`, getAuthHeaders());
    heatmapSelection.value = res.data.product_codes || [];
    if (res.data.vista) {
      vistaActual.value = res.data.vista;
      localStorage.setItem('vista_actual', res.data.vista);
    }
  } catch (e) { console.error(e); }
};

const loadActiveHeatmapCodes = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/saved-heatmaps/`, getAuthHeaders());
    const codes = new Set();
    res.data.filter(h => h.is_active).forEach(h => {
      (h.product_codes || []).forEach(c => codes.add(String(c)));
    });
    activeHeatmapCodes.value = codes;
  } catch (e) { /* ignore */ }
};

const loadConteo = async () => {
  for (const v of ['web', 'marketplace']) {
    try {
      const res = await axios.get(`${API_BASE_URL}/kpis/overview`, {
        ...getAuthHeaders(), params: { vista: v }
      });
      conteoVistas.value[v] = res.data.competitive_products_count || 0;
    } catch (e) { /* ignore */ }
  }
};

watch(vistaActual, () => loadProductos());

onMounted(async () => {
  const token = localStorage.getItem('token_precios');
  if (!token) { router.push('/'); return; }
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    userInfo.value = { username: payload.sub || 'admin', rol: payload.rol || 'admin' };
  } catch { /* ignore */ }

  await Promise.all([loadProductos(), loadActiveHeatmapCodes()]);
  loadConteo();
});
</script>

<style scoped>
#app-container { min-height: 100vh; background: var(--color-background); color: var(--color-text); }
.view-content {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 1800px;
  margin: 0 auto;
}
.view-header {
  padding: 4px 0 14px;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 6px;
}
.view-kicker {
  display: block;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.view-title {
  margin: 4px 0 4px;
  font-family: 'Inter', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: -0.01em;
}
.view-meta {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--color-text-muted);
  letter-spacing: 0.03em;
}

/* Panel exploración */
.exploration-panel {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  overflow: hidden;
}
.main-tabs-bar {
  display: flex;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
  padding: 0 12px;
}
.main-tab {
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 10px 16px;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.main-tab:hover { color: var(--color-text); }
.main-tab.active {
  color: var(--color-accent);
  border-bottom-color: var(--color-accent);
  background: var(--color-accent-bg);
}
.tab-badge {
  background: var(--color-accent);
  color: var(--color-surface-3);
  padding: 1px 6px;
  border-radius: 2px;
  font-size: 9.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.guardados-tab { padding: 14px; }

/* Layout productos/detalle (igual que antes) */
:deep(.dashboard-layout) {
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 12px;
  align-items: flex-start;
  padding: 12px;
  margin: 0;
}
:deep(.product-list-panel) {
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 75vh;
  overflow: hidden;
}
:deep(.product-list-panel h2) {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-accent);
  text-transform: uppercase;
  margin: 0;
  padding: 4px 0;
  border-bottom: 1px solid var(--color-accent-border);
}
:deep(.filter-controls) {
  display: flex;
  gap: 3px;
  flex-wrap: wrap;
}
:deep(.filter-controls button) {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  padding: 5px 10px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
:deep(.filter-controls button.active) {
  background: var(--color-accent-bg);
  color: var(--color-accent);
  border-color: var(--color-accent-border);
}
:deep(.filter-count) {
  font-size: 9.5px;
  background: var(--color-surface-3);
  padding: 1px 5px;
  border-radius: 2px;
}
:deep(.search-bar) {
  width: 100%;
  padding: 7px 10px;
  border-radius: 2px;
  border: 1px solid var(--color-border-strong);
  background: var(--color-background);
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: 11px;
  box-sizing: border-box;
}
:deep(.product-list) {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: 2px;
  background: var(--color-background);
}
:deep(.product-list li) {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
  font-size: 12px;
  color: var(--color-text);
}
:deep(.product-list li:hover) { background: var(--color-hover-row); }
:deep(.product-list li.selected) {
  background: var(--color-accent-bg);
  border-left: 3px solid var(--color-accent);
  padding-left: 7px;
}
:deep(.product-code) {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--color-text-faint);
}
:deep(.canasta-checkbox) { accent-color: var(--color-accent); cursor: pointer; }
:deep(.active-hm-dot) {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-success);
}

:deep(.product-detail-panel) {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  min-height: 400px;
}
:deep(.tab-content) { padding: 14px; }
:deep(.placeholder) {
  padding: 40px 20px;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 12px;
  font-style: italic;
  background: var(--color-surface-2);
  border: 1px dashed var(--color-border-strong);
  border-radius: 2px;
}
:deep(.heatmap-constructor) {
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  border-left: 3px solid var(--color-accent);
  border-radius: 2px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
:deep(.heatmap-constructor h3) {
  margin: 0 0 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-accent);
  text-transform: uppercase;
}
:deep(.heatmap-constructor .controls) {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
:deep(.heatmap-constructor button) {
  background: transparent;
  border: 1px solid var(--color-border-strong);
  color: var(--color-text-muted);
  padding: 5px 11px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
:deep(.heatmap-constructor .generate-btn:hover) {
  color: var(--color-accent);
  border-color: var(--color-accent);
  background: var(--color-accent-bg);
}
:deep(.heatmap-constructor .save-btn:hover) {
  color: var(--color-success);
  border-color: var(--color-success);
}
:deep(.heatmap-constructor .clear-btn:hover) {
  color: var(--color-danger);
  border-color: var(--color-danger);
}
:deep(.empty-heatmap) {
  padding: 30px 20px;
  text-align: center;
  background: var(--color-surface-2);
  border: 1px dashed var(--color-border-strong);
  border-radius: 2px;
}
:deep(.heatmap-preview) {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
}
:deep(.preview-section) {
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  border-radius: 2px;
  padding: 10px 12px;
}
:deep(.preview-title) {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: var(--color-text-muted);
  text-transform: uppercase;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
:deep(.count-chip) {
  background: var(--color-accent-bg);
  color: var(--color-accent);
  border: 1px solid var(--color-accent-border);
  padding: 1px 7px;
  border-radius: 2px;
  font-size: 10px;
  font-weight: 700;
}
:deep(.selected-products) {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
:deep(.selected-products li) {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 2px;
  font-size: 11px;
}
:deep(.remove-btn) {
  background: transparent;
  border: 1px solid var(--color-border-strong);
  color: var(--color-text-faint);
  padding: 1px 6px;
  border-radius: 2px;
  cursor: pointer;
}
:deep(.remove-btn:hover) {
  color: var(--color-danger);
  border-color: var(--color-danger);
}
</style>